import { useMemo, useState } from 'react';
import { CalendarHeart } from 'lucide-react';
import { HOLIDAY_REGIONS, holidaysOf, type HolidayRegion } from '@kaba/shared';
import { accountPref, setAccountPref } from '../lib/boardNav';
import { cx } from './ui/ui';

const sel = 'min-h-11 max-w-full min-w-0 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:min-h-9 sm:text-sm';

/** 0.69: Bei Arbeitstagen die Feiertage eines Bundeslands auslassen (zuletzt gewähltes wird vorgeschlagen) */
export function HolidaySelect({ value, onChange }: { value: HolidayRegion | null | undefined; onChange: (r: HolidayRegion | null) => void }) {
  return (
    <label className="flex min-w-0 max-w-full items-center gap-1.5 text-sm">
      ohne Feiertage in
      <select
        aria-label="Feiertage"
        className={cx(sel, 'min-w-0 max-w-full flex-1')}
        value={value ?? ''}
        onChange={(e) => {
          const r = (e.target.value || null) as HolidayRegion | null;
          if (r) setAccountPref('holidayRegion', r);
          onChange(r);
        }}
      >
        <option value="">– (nur Wochenenden)</option>
        {HOLIDAY_REGIONS.map(([k, name]) => (
          <option key={k} value={k}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * 0.73/0.75: Feiertage in Kalender und Zeitleiste ein- und ausblenden. Ein/Aus je Ansicht
 * (`prefKey`), das Bundesland ist für alle Ansichten, Serien und Regeln dasselbe (`holidayRegion`).
 */
export function useHolidayDisplay(prefKey: string, years: number[]) {
  const [show, setShow] = useState(() => accountPref<boolean>(prefKey, false));
  const [region, setRegion] = useState<HolidayRegion>(() => accountPref<HolidayRegion | null>('holidayRegion', null) ?? 'DE');
  const key = years.join(',');
  const holidays = useMemo(() => {
    const m = new Map<string, string>();
    if (!show) return m;
    for (const y of new Set(years)) for (const [k, v] of holidaysOf(y, region)) m.set(k, v);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, region, key]);
  return {
    show,
    region,
    holidays,
    toggle: () => {
      setShow(!show);
      setAccountPref(prefKey, !show);
    },
    setRegion: (r: HolidayRegion) => {
      setRegion(r);
      setAccountPref('holidayRegion', r);
    },
  };
}

/** Knopf „Feiertage“ (aria-pressed) und – eingeschaltet – die Auswahl des Bundeslands */
export function HolidayToggle({ display, className }: { display: ReturnType<typeof useHolidayDisplay>; className?: string }) {
  return (
    <div className={cx('flex items-center gap-1', className)}>
      <button
        type="button"
        aria-pressed={display.show}
        title="Feiertage zeigen"
        onClick={display.toggle}
        className={cx(
          'inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2.5 text-sm sm:min-h-9',
          display.show ? 'bg-accent-soft font-medium' : 'text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10',
        )}
      >
        <CalendarHeart size={16} /> Feiertage
      </button>
      {display.show && (
        <select
          aria-label="Feiertage für"
          value={display.region}
          onChange={(e) => display.setRegion(e.target.value as HolidayRegion)}
          className="min-h-10 max-w-36 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:min-h-9 sm:text-sm"
        >
          {HOLIDAY_REGIONS.map(([k, name]) => (
            <option key={k} value={k}>
              {name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
